/**
 * Reads all files contained in a DataTransferItemList.
 * It supports reading multiple files and directories.
 * It will recursively read all files from the directories.
 *
 * @param items DataTransferItemList from a drag&drop event
 */
export async function readAllFiles(items: DataTransferItemList): Promise<File[]> {
	// Explicitly copy and convert to array. Needed for .map call
	const promises = [...items].map(item => processDataTransferItem(item));

	// If we would sequentially await/process the DataTransferItems, then we would lose the underlying file handle, and
	// we could only process the first item that is drag & dropped.
	// It needs to be in the same event loop to be able to read all the files / directories.
	const allFiles: File[][] = await Promise.all(promises)
	const flatFiles: File[] = [];
	allFiles.forEach(files => flatFiles.push(...files));

	return flatFiles;
}

async function processDataTransferItem(item: DataTransferItem): Promise<File[]> {
	if (item.kind === "file") {
		let entry: FileSystemEntry;
		if (item.webkitGetAsEntry) {
			entry = item.webkitGetAsEntry();
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-explicit-any
		} else if ((item as any).getAsEntry) {
			// Future proofing for rename as per documentation of webkitGetAsEntry
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-explicit-any
			entry = (item as any).getAsEntry();
		}

		if (entry) {
			return processFileSystemEntry(entry);
		} else {
			// Fallback if webkitGetAsEntry not available
			const file = item.getAsFile();
			if (file) {
				return Promise.resolve([file]);
			}
		}
	}

	return Promise.resolve([] as File[]);
}

async function processFileSystemEntry(entry: FileSystemEntry, path = ""): Promise<File[]> {
	const allFiles: File[] = [];

	async function recursion(entry: FileSystemEntry, path = "") {
		if (isFile(entry)) {
			await new Promise<void>((resolve, reject) => {
				entry.file(
					(file: File) => {
						allFiles.push(file);
						resolve();
					},
					(err: DOMException) => reject(err)
				);
			});
		} else if (isDirectory(entry)) {
			const dirReader = entry.createReader();

			async function processAll(entries: FileSystemEntry[]) {
				for (const e of entries) {
					if (e.name.startsWith(".")) {
						// Ignore macOS & Linux hidden files when enumerating directories.
						// Do it here so it's still possible to explicitly drag&drop them.
						continue;
					}

					await recursion(e, path + entry.name + "/");
				}
			}

			await new Promise<void>((resolve, reject) => {
				dirReader.readEntries(
					(entries: FileSystemEntry[]) => {
						processAll(entries).then(
							() => resolve(),
							(err: DOMException) => reject(err)
						)
					}, (err: DOMException) => reject(err));
			});
		}
	}

	await recursion(entry, path);

	return allFiles;
}

/**
 * Checks if a given file matches an `accept` string like in HTML input[type="file"]
 *
 * @param file - The File object to check
 * @param accept - The accept string (e.g., "image/*,.pdf,application/json")
 * @returns true if the file matches the accept criteria, false otherwise
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept
 */
export function isFileAccepted(file: File, accept: string): boolean {
	if (!accept) return true; // No restriction

	const acceptItems = accept
		.split(",")
		.map(item => item.trim().toLowerCase())
		.filter(Boolean);

	const fileName = file.name.toLowerCase();
	const fileType = file.type.toLowerCase();

	for (const item of acceptItems) {
		if (item.startsWith(".")) {
			// Match by file extension
			if (fileName.endsWith(item)) {
				return true;
			}
		} else if (item.endsWith("/*")) {
			// Match by MIME type group (e.g., image/*)
			const [typeMain] = fileType.split("/");
			const [acceptMain] = item.split("/");
			if (typeMain === acceptMain) {
				return true;
			}
		} else {
			// Match by full MIME type (e.g., image/png)
			if (fileType === item) {
				return true;
			}
		}
	}

	return false;
}

// Type guards for FileSystemEntry
function isFile(entry: FileSystemEntry): entry is FileSystemFileEntry {
	return entry.isFile;
}

function isDirectory(entry: FileSystemEntry): entry is FileSystemDirectoryEntry {
	return entry.isDirectory;
}
